import type { SessionProfile } from "../../shared/types/domain";
import { demoOrganizationRepository } from "../organization/demoOrganizationRepository";
import { demoSchedulingRepository } from "../scheduling/demoSchedulingRepository";
import { demoEnergyLedgerAdapter } from "../incentives/demoIncentivesRepository";
import { demoNotificationAdapter } from "../governance/demoGovernanceRepository";
import type { LearningState } from "./learning.types";

const STORAGE_KEY = "boom-v4-learning";
const seed: LearningState = { feedback: [], ratings: [], lowRatingCases: [], growthMedia: [], feedbackTasks: [] };
const clone = <T>(value: T): T => structuredClone(value);
function load(): LearningState {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return clone(seed);
  try {
    const parsed = JSON.parse(raw) as LearningState;
    return { ...parsed, growthMedia: parsed.growthMedia ?? [], feedbackTasks: parsed.feedbackTasks ?? [] };
  } catch {
    return clone(seed);
  }
}
function save(state: LearningState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}
async function loadWithTasks() {
  const state = load(); const scheduling = await demoSchedulingRepository.getState(); const now = Date.now();
  for (const consumption of scheduling.consumptions.filter((item) => item.status === "active")) {
    const lesson = scheduling.schedules.find((item) => item.id === consumption.scheduleId); if (!lesson) continue;
    let task = state.feedbackTasks.find((item) => item.scheduleId === lesson.id);
    const published = state.feedback.find((item) => item.scheduleId === lesson.id);
    if (!task) { task = { id: crypto.randomUUID(), scheduleId: lesson.id, memberId: lesson.memberId, coachId: lesson.coachId, storeId: lesson.storeId, dueAt: new Date(new Date(consumption.completedAt).getTime() + 24 * 60 * 60 * 1000).toISOString(), status: "pending", feedbackId: null, createdAt: consumption.completedAt }; state.feedbackTasks.unshift(task); }
    task.feedbackId = published?.id ?? null; task.status = published ? "published" : new Date(task.dueAt).getTime() < now ? "overdue" : "pending";
    if (task.status === "overdue") demoNotificationAdapter.notify({ recipientId: task.coachId, storeId: task.storeId, type: "feedback", title: "课后反馈已逾期", content: "该课程完成已超过24小时，请尽快补发成长反馈。", entityType: "feedback_task", entityId: task.id, dedupeKey: `feedback_task:${task.id}:feedback` });
  }
  save(state); return state;
}
function fail(message: string): never {
  throw new Error(message);
}
async function activeActor(actor: SessionProfile) {
  const org = await demoOrganizationRepository.getState();
  const account = org.accounts.find((item) => item.id === actor.id);
  if (!account || account.status !== "active") fail("当前账号不可用。");
  return org;
}

export const demoLearningRepository = {
  async getState() {
    return loadWithTasks();
  },
  async reset() {
    localStorage.removeItem(STORAGE_KEY);
    return clone(seed);
  },
  async submitFeedback(
    actor: SessionProfile,
    input: {
      scheduleId: string;
      summary: string;
      strengths: string;
      improvements: string;
      homework: string;
    },
  ) {
    await activeActor(actor);
    if (!["employee", "manager"].includes(actor.role))
      fail("只有授课教练可以提交课后反馈。");
    const scheduling = await demoSchedulingRepository.getState();
    const lesson =
      scheduling.schedules.find((item) => item.id === input.scheduleId) ??
      fail("课程不存在。");
    if (lesson.status !== "completed")
      fail("只有已完成且未撤销的课程可以填写反馈。");
    if (lesson.coachId !== actor.id) fail("只能填写本人授课课程的反馈。");
    if (
      !input.summary.trim() ||
      !input.strengths.trim() ||
      !input.improvements.trim()
    )
      fail("课程小结、表现亮点和提升建议不能为空。");
    const state = await loadWithTasks();
    const now = new Date().toISOString();
    const existing = state.feedback.find(
      (item) => item.scheduleId === lesson.id,
    );
    if (existing) {
      Object.assign(existing, {
        summary: input.summary.trim(),
        strengths: input.strengths.trim(),
        improvements: input.improvements.trim(),
        homework: input.homework.trim(),
        updatedAt: now,
      });
      save(state);
      return clone(existing);
    }
    const next = {
      id: crypto.randomUUID(),
      scheduleId: lesson.id,
      memberId: lesson.memberId,
      coachId: lesson.coachId,
      storeId: lesson.storeId,
      summary: input.summary.trim(),
      strengths: input.strengths.trim(),
      improvements: input.improvements.trim(),
      homework: input.homework.trim(),
      status: "published" as const,
      createdAt: now,
      updatedAt: now,
    };
    state.feedback.unshift(next);
    const task = state.feedbackTasks.find((item) => item.scheduleId === lesson.id);
    if (task) { task.status = "published"; task.feedbackId = next.id; }
    save(state);
    demoNotificationAdapter.notify({
      recipientId: next.memberId,
      storeId: next.storeId,
      type: "feedback",
      title: "课后反馈已发布",
      content: "教练已发布本节课程反馈，可以查看并评价。",
      entityType: "teaching_feedback",
      entityId: next.id,
      dedupeKey: `teaching_feedback:${next.id}:feedback`,
    });
    return clone(next);
  },
  async rateLesson(
    actor: SessionProfile,
    input: {
      feedbackId: string;
      professionalScore: number;
      serviceScore: number;
      effectScore: number;
      experienceScore: number;
      tags: string[];
      comment: string;
    },
  ) {
    await activeActor(actor);
    if (actor.role !== "member") fail("只有会员可以评价课程。");
    const dimensions = [input.professionalScore, input.serviceScore, input.effectScore, input.experienceScore];
    const limits = [30, 25, 30, 15];
    if (dimensions.some((value, index) => !Number.isInteger(value) || value < 0 || value > limits[index])) fail("四维评分必须为有效整数且不能超过各维度满分。");
    const totalScore = dimensions.reduce((sum, value) => sum + value, 0);
    const state = load();
    const feedback =
      state.feedback.find((item) => item.id === input.feedbackId) ??
      fail("课后反馈不存在。");
    if (feedback.memberId !== actor.id) fail("只能评价本人的课程。");
    if (state.ratings.some((item) => item.feedbackId === feedback.id))
      fail("本节课程已经评价。");
    if (totalScore < 80 && input.comment.trim().length < 2) fail("低于80分必须填写具体原因。");
    const rating = {
      id: crypto.randomUUID(),
      feedbackId: feedback.id,
      scheduleId: feedback.scheduleId,
      memberId: actor.id,
      storeId: feedback.storeId,
      professionalScore: input.professionalScore, serviceScore: input.serviceScore,
      effectScore: input.effectScore, experienceScore: input.experienceScore,
      totalScore, stars: Math.max(1, Math.ceil(totalScore / 20)),
      tags: [...new Set(input.tags)].slice(0, 5),
      comment: input.comment.trim(),
      createdAt: new Date().toISOString(),
    };
    state.ratings.unshift(rating);
    if (rating.totalScore < 80)
      state.lowRatingCases.unshift({
        id: crypto.randomUUID(),
        ratingId: rating.id,
        feedbackId: feedback.id,
        memberId: actor.id,
        coachId: feedback.coachId,
        storeId: feedback.storeId,
        status: "open",
        priority: rating.totalScore < 60 ? "urgent" : "high",
        ownerId: null,
        resolution: "",
        action: "",
        createdAt: rating.createdAt,
        resolvedAt: null,
      });
    save(state);
    if (rating.totalScore >= 95)
      demoEnergyLedgerAdapter.awardAutomatic(
        feedback.coachId,
        feedback.storeId,
        "POSITIVE_FEEDBACK",
        rating.id,
      );
    return clone(rating);
  },
  async resolveLowRating(
    actor: SessionProfile,
    input: { caseId: string; resolution: string; action: string },
  ) {
    await activeActor(actor);
    if (!["manager", "super"].includes(actor.role))
      fail("只有店长或超管可以处理低评分。");
    const state = load();
    const target =
      state.lowRatingCases.find((item) => item.id === input.caseId) ??
      fail("低评分事项不存在。");
    if (target.status !== "open") fail("该事项已经处理。");
    if (actor.role === "manager" && actor.storeId !== target.storeId)
      fail("不能处理其他门店事项。");
    if (!input.resolution.trim() || !input.action.trim())
      fail("必须填写沟通结论和改进动作。");
    target.status = "resolved";
    target.ownerId = actor.id;
    target.resolution = input.resolution.trim();
    target.action = input.action.trim();
    target.resolvedAt = new Date().toISOString();
    save(state);
    return clone(target);
  },
  async uploadGrowthMedia(
    actor: SessionProfile,
    input: { feedbackId: string; file: File; caption: string },
  ) {
    await activeActor(actor);
    if (!["employee", "manager"].includes(actor.role))
      fail("只有授课教练可以上传成长影像。");
    const state = load();
    const feedback = state.feedback.find((item) => item.id === input.feedbackId) ?? fail("课后反馈不存在。");
    if (feedback.coachId !== actor.id) fail("只能为本人授课记录上传成长影像。");
    if (!input.file.type.startsWith("image/") && !input.file.type.startsWith("video/")) fail("只支持图片或视频。");
    if (input.file.size > 50 * 1024 * 1024) fail("单个文件不能超过50MB。");
    const media = {
      id: crypto.randomUUID(), feedbackId: feedback.id, memberId: feedback.memberId,
      coachId: actor.id, storeId: feedback.storeId,
      mediaType: input.file.type.startsWith("video/") ? "video" as const : "image" as const,
      fileName: input.file.name, mimeType: input.file.type, fileSize: input.file.size,
      caption: input.caption.trim(), storagePath: `demo/${input.file.name}`,
      previewUrl: URL.createObjectURL(input.file), createdAt: new Date().toISOString(),
    };
    state.growthMedia.unshift(media);
    save(state);
    return clone(media);
  },
  async deleteGrowthMedia(actor: SessionProfile, input: { mediaId: string; reason: string }) {
    await activeActor(actor);
    const state = load();
    const index = state.growthMedia.findIndex((item) => item.id === input.mediaId);
    if (index < 0) fail("成长影像不存在。");
    const target = state.growthMedia[index];
    const allowed = actor.role === "super" || (actor.role === "manager" && actor.storeId === target.storeId) || (actor.role === "employee" && actor.id === target.coachId);
    if (!allowed) fail("无权删除该成长影像。");
    if (input.reason.trim().length < 2) fail("请填写删除原因。");
    state.growthMedia.splice(index, 1);
    save(state);
    return clone(target);
  },
};
